/*
USAGE EXAMPLES:

1. Non-Production Environment (Default Behavior):
   - Logging is disabled in production but enabled in development or other non-production environments.
   - Errors are always logged regardless of the environment.

import saltus from './logger';

saltus.log('This will log only in non-production environments');
saltus.error('This will always log errors, even in production');

2. Production Environment (With Optional Logging Enabled):
   - You can create a custom logger instance to enable logging in production by passing the `allowLoggingInProduction` flag.
   - This is useful for debugging purposes during production but should be used cautiously to avoid unnecessary logging.

import { createSaltus } from './logger';

// Create an instance of saltus that allows logging in production
const saltusWithProdLogging = createSaltus({ allowLoggingInProduction: true });

saltusWithProdLogging.log('This will log even in production');
saltusWithProdLogging.warn('This will also log in production');

*/

const isProduction: boolean = process.env.NODE_ENV === 'production';

interface SaltusConfig {
  allowLoggingInProduction?: boolean;
}

interface Saltus {
  log: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

const createSaltus = (config: SaltusConfig = {}): Saltus => {
  const {allowLoggingInProduction = false} = config; // Default is false

  // Determine if logging should be enabled in production based on the config
  const shouldLog = !isProduction || allowLoggingInProduction;

  return {
    log: (...args: unknown[]): void => {
      if (shouldLog) {
        console.log(...args);
      }
    },
    info: (...args: unknown[]): void => {
      if (shouldLog) {
        console.info(...args);
      }
    },
    warn: (...args: unknown[]): void => {
      if (shouldLog) {
        console.warn(...args);
      }
    },
    error: (...args: unknown[]): void => {
      console.error(...args); // Always logs errors regardless of the environment
    },
  };
};

// Create the default saltus instance (without allowing production logs)
export const saltus = createSaltus();

export {createSaltus};
export default saltus;
